import { FEAT, formFx, REACTIONS, SKILLS, type DamageType } from '@somnhold/content';
import { cellAtWorld, cellHex } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { cosA, dist2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Bot, Enemy, Hero, UqDue, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, isFlying, targetable } from './combat.ts';
import { featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { due, heroById, pieceStun } from './panoply.ts';
import { fx, hasUq } from './uniques.ts';

/**
 * The mage's panoply in a fight (docs/PANOPLIA.md §10), as battle/panoply.ts does the warrior's: each piece changes its
 * skill where the skill's program or its zone calls it (a zone of a piece carries its id in `uq`), what runs from one
 * nightmare to the next waits in the pieces' queue, and what the feats ask is counted here, a line each.
 */

const heroOfPid = (s: BattleState, pid: string): Hero | undefined => s.heroes.find((h) => h.pid === pid);
const hits = (s: BattleState, x: number, y: number, r: number): Enemy[] => enemiesNear(s, x, y, r, (o) => targetable(s, o));
/** The cascades of the pieces go three generations at most (docs/PANOPLIA.md §8). */
const GENS = 3;

/* ---------- Báculo de las tormentas (Cadena de rayos) ---------- */

/**
 * The chain is done: the bolt comes back along its way next tick (`dmg` of the skill's blow at the piece's share) and,
 * with Rayo partido, a second chain leaves the first one caught. A chain of five or more is watched for the test.
 */
export const boltBack = (s: BattleState, h: Hero, chain: Enemy[], dmg: number, jumps: number, fall: number, radius: number, type: DamageType): void => {
  if (!hasUq(h, 'stormStaff') || !chain.length) return;
  const last = chain[chain.length - 1] as Enemy;
  const tc = type === 'lightning' ? 0 : 1;
  if (chain.length > 1) {
    const back = chain.slice(0, -1).reverse().map((o) => o.id);
    due(s, { at: s.tick + 1, k: 'boltBack', hid: h.id, x: last.x, y: last.y, n: [mulBp(dmg, fx('stormStaff', h).damageBp as number), tc], ids: back });
  }
  if (formFinal(h, 'stormStaff') && jumps > 1) {
    const first = chain[0] as Enemy;
    due(s, { at: s.tick + 1, k: 'boltSplit', hid: h.id, x: first.x, y: first.y, n: [mulBp(dmg, fall), jumps - 1, fall, radius, tc], ids: chain.map((o) => o.id) });
  }
  if (chain.length >= FEAT.need.chainKills) featPieceWatch(s, h, 'stormStaff', chain.map((o) => o.id), FEAT.watch.chain, FEAT.need.chainKills);
};

const boltBackGo = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, tc = 0] = d.n ?? [];
  let from = { x: d.x, y: d.y };
  let n = 0;
  for (const id of d.ids ?? []) {
    const e = s.enemies.find((o) => o.id === id);
    if (!e || e.hp <= 0 || !targetable(s, e)) continue;
    damageEnemy(s, e, dmg, tc ? { pid: h.pid } : { pid: h.pid, type: 'lightning' });
    ev(s, { t: 'beam', x: from.x, y: from.y, tx: e.x, ty: e.y, kind: 'boltBack' });
    from = { x: e.x, y: e.y };
    n++;
  }
  featUse(s, h, 'stormStaff', n);
};

const boltSplit = (s: BattleState, h: Hero, d: UqDue): void => {
  let [dmg = 0] = d.n ?? [];
  const [, jumps = 0, fall = 10000, radius = 0, tc = 0] = d.n ?? [];
  const hit = new Set(d.ids ?? []);
  let from = { x: d.x, y: d.y };
  for (let i = 0; i < jumps; i++) {
    const cur = enemiesNear(s, from.x, from.y, radius, (o) => targetable(s, o) && !hit.has(o.id))[0];
    if (!cur) break;
    hit.add(cur.id);
    damageEnemy(s, cur, dmg, tc ? { pid: h.pid } : { pid: h.pid, type: 'lightning' });
    ev(s, { t: 'beam', x: from.x, y: from.y, tx: cur.x, ty: cur.y, kind: 'boltSplit' });
    from = { x: cur.x, y: cur.y };
    dmg = mulBp(dmg, fall);
  }
};

/* ---------- Báculo del destilador (Dardo de ácido) ---------- */

/** Goteo: the eaten one drips a while; a drop each `every` (`dmg`: the dart's blow). */
export const dripOn = (s: BattleState, h: Hero, e: Enemy | null, dmg: number, gen = 0): void => {
  if (!e || e.hp <= 0 || !hasUq(h, 'distillerStaff')) return;
  const f = fx('distillerStaff', h);
  e.dripBy = h.id;
  e.dripDmg = dmg;
  e.uqGen = gen;
  if (!gen) h.dripRun = [e.id, 0, 0];
  for (let t = f.every as number; t <= (f.ticks as number); t += f.every as number) due(s, { at: s.tick + t, k: 'drip', hid: h.id, x: 0, y: 0, n: [e.id, h.dripRun?.[0] ?? e.id] });
};

const dripFalls = (s: BattleState, h: Hero, d: UqDue): void => {
  const [eid = 0, run = 0] = d.n ?? [];
  const e = s.enemies.find((o) => o.id === eid);
  if (!e || e.hp <= 0 || e.dripBy !== h.id) return;
  const f = fx('distillerStaff', h);
  // The cap: the oldest drop of this hero dries.
  const mine = s.zones.filter((z) => z.kind === 'drip' && z.pid === h.pid);
  if (mine.length >= (f.cap as number)) {
    const old = mine[0] as Zone;
    s.zones = s.zones.filter((z) => z !== old);
  }
  s.zones.push({ id: s.nextId++, kind: 'drip', x: e.x, y: e.y, radius: f.radius as number, cells: [], ticks: 120, every: 3, damage: mulBp(e.dripDmg ?? 0, f.damageBp as number), slowBp: 0, pid: h.pid, uq: 'distillerStaff', fe: e.id, uqn: [run] });
};

/** A drop: the first nightmare on the ground that steps on it (not the one dripping) is eaten, and the drop is spent. */
const dropTick = (s: BattleState, z: Zone, h: Hero): void => {
  if (z.ticks <= 0 || (s.tick + z.id) % 3 !== 0) return;
  const e = enemiesNear(s, z.x, z.y, z.radius, (o) => o.id !== z.fe && targetable(s, o) && !isFlying(o))[0];
  if (!e) return;
  damageEnemy(s, e, z.damage, { pid: z.pid, type: 'acid' });
  if ((e.acidTicks ?? 0) < SKILLS.acidDart.duration) e.acidTicks = SKILLS.acidDart.duration;
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'dripEat' });
  z.ticks = 0;
  featUse(s, h, 'distillerStaff', 1);
  const r = h.dripRun;
  if (r && r[0] === z.uqn?.[0] && !r[2]) {
    r[1] = (r[1] ?? 0) + 1;
    if ((r[1] ?? 0) >= FEAT.need.drip) {
      r[2] = 1;
      featPieceTest(s, h, 'distillerStaff');
    }
  }
};

/** Dardo que sigue: the eaten one died with the acid on it; the dart flies on to the nearest. */
const dartAgain = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, gen = 1] = d.n ?? [];
  const range = formFx('distillerStaff', 'final').range as number;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of hits(s, d.x, d.y, range)) {
    const dd = dist2(o.x, o.y, d.x, d.y);
    if (dd < bd) {
      bd = dd;
      best = o;
    }
  }
  if (!best) return;
  damageEnemy(s, best, dmg, { pid: h.pid, type: 'acid' });
  if ((best.acidTicks ?? 0) < SKILLS.acidDart.duration) best.acidTicks = SKILLS.acidDart.duration;
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: best.x, ty: best.y, kind: 'acidDart' });
  dripOn(s, h, best, dmg, gen);
};

/* ---------- Báculo de la despensa (Lanza de escarcha) ---------- */

/** Huella helada: the lance's line stays frozen (its cells, a hex wide); a lance through six or more is the test. */
export const iceTrail = (s: BattleState, h: Hero, end: { x: number; y: number }, pierced: number): void => {
  if (!hasUq(h, 'pantryStaff')) return;
  const cells: number[] = [];
  const len = isqrt(dist2(h.x, h.y, end.x, end.y));
  for (let d = 0; d <= len; d += 500) {
    const c = cellAtWorld(s, h.x + idiv((end.x - h.x) * d, imax(1, len)), h.y + idiv((end.y - h.y) * d, imax(1, len)));
    if (c >= 0 && !cells.includes(c)) cells.push(c);
  }
  if (cells.length) s.zones.push({ id: s.nextId++, kind: 'iceTrail', x: end.x, y: end.y, radius: 0, cells, ticks: fx('pantryStaff', h).ticks as number, every: 3, damage: 0, slowBp: 0, pid: h.pid, uq: 'pantryStaff' });
  if (pierced >= FEAT.need.lance) featPieceTest(s, h, 'pantryStaff');
};

/** The frozen line: what walks on it is chilled (counted when it wasn't). */
const trailTick = (s: BattleState, z: Zone, h: Hero): void => {
  if (z.ticks <= 0 || s.tick % 3 !== 0) return;
  let n = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || isFlying(e) || (s.dragon && e.id === s.dragon.id) || !z.cells.includes(cellAtWorld(s, e.x, e.y))) continue;
    if ((e.chill ?? 0) <= s.tick) n++;
    e.chill = s.tick + REACTIONS.chillTicks;
  }
  featUse(s, h, 'pantryStaff', n);
};

/** Témpano: a shatter on a frozen line of a piece in its final form reaches all that is on the line (a shard). */
export const icicle = (s: BattleState, e: Enemy, burst: number, pid: string): void => {
  for (const z of s.zones) {
    if (z.kind !== 'iceTrail' || z.ticks <= 0) continue;
    const c = cellAtWorld(s, e.x, e.y);
    if (!z.cells.includes(c)) continue;
    const h = heroOfPid(s, z.pid);
    if (!h || !formFinal(h, 'pantryStaff')) continue;
    for (const o of s.enemies) {
      if (o.hp <= 0 || o.id === e.id || !targetable(s, o) || !z.cells.includes(cellAtWorld(s, o.x, o.y))) continue;
      damageEnemy(s, o, burst, { pid, type: 'frost', ignoresArmor: true, noReact: true, shard: true });
    }
    ev(s, { t: 'boom', x: z.x, y: z.y, r: 600, kind: 'icicle' });
    return;
  }
};

/* ---------- Túnica del muro blanco (Muro de escarcha) ---------- */

/** Muro que estalla: as the wall ends, its shards chill what is near it; with its final form a shorter wall rises. */
const wallBursts = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('whiteWallRobe', h);
  const R = s.map.radius;
  const seen = new Set<number>();
  for (const id of z.cells) {
    const c = cellHex(R, id);
    const w = hexToWorld(c.q, c.r);
    for (const e of hits(s, w.x, w.y, f.radius as number)) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      damageEnemy(s, e, f.damage as number, { pid: z.pid, type: z.dt ?? 'frost' });
    }
    ev(s, { t: 'boom', x: w.x, y: w.y, r: f.radius as number, kind: 'wallBurst' });
  }
  featUse(s, h, 'whiteWallRobe', seen.size);
  if (seen.size >= FEAT.need.wallBurst) featPieceTest(s, h, 'whiteWallRobe');
  if (!formFinal(h, 'whiteWallRobe')) return;
  const ff = formFx('whiteWallRobe', 'final');
  const n = imax(1, idiv(z.cells.length * (ff.lenBp as number) + 9999, 10000));
  const back = idiv(z.cells.length - n, 2);
  const again: Zone = { ...z, id: s.nextId++, cells: z.cells.slice(back, back + n), ticks: ff.ticks as number };
  delete again.uq;
  s.zones.push(again);
};

/* ---------- Túnica de la maestra (Orbe helado) ---------- */

/** Ida y vuelta: where the orb arrives it turns back toward the mage the same way (once), biting at the piece's share. */
export const orbTurn = (s: BattleState, z: Zone): void => {
  const t = (z.uqn ??= [(z.steps ?? 0) + 1, z.radius, 0]);
  if (z.hop || (z.steps ?? 0) > 0) return;
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  z.hop = true;
  z.vx = -(z.vx ?? 0);
  z.vy = -(z.vy ?? 0);
  z.steps = t[0];
  z.ticks += t[0] ?? 0;
  z.damage = mulBp(z.damage, fx('teacherRobe', h).damageBp as number);
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'orbTurn' });
};

/** A nightmare in the orb (`dealt`: its bite, if one fell): the return's counted, each new one grows it with Orbe que crece. */
export const orbTouched = (s: BattleState, z: Zone, e: Enemy, dealt: number): void => {
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  if (z.hop && dealt > 0) featUse(s, h, 'teacherRobe', dealt);
  const seen = (z.hit ??= []);
  if (seen.includes(e.id)) return;
  seen.push(e.id);
  const t = (z.uqn ??= [(z.steps ?? 0) + 1, z.radius, 0]);
  if (formFinal(h, 'teacherRobe')) {
    const f = formFx('teacherRobe', 'final');
    const base = t[1] ?? z.radius;
    z.radius = imin(mulBp(base, f.maxBp as number), z.radius + mulBp(base, f.stepBp as number));
  }
  if (!t[2] && seen.length >= FEAT.need.orb) {
    t[2] = 1;
    featPieceTest(s, h, 'teacherRobe');
  }
};

/* ---------- Sobreveste del carcelero (Cadenas de sombra) ---------- */

/** The binding is the jailer's: its deaths will be shared. A binding of four or more is watched for the test (all must fall). */
export const jailLinks = (s: BattleState, h: Hero, bound: Enemy[], ticks: number): void => {
  if (!hasUq(h, 'jailerSurcoat')) return;
  for (const o of bound) {
    o.jailBy = h.id;
    o.uqGen = 0;
  }
  if (bound.length >= FEAT.need.binding) featPieceWatch(s, h, 'jailerSurcoat', bound.map((o) => o.id), ticks + FEAT.watch.binding, bound.length);
};

const shareDeath = (s: BattleState, h: Hero, d: UqDue): void => {
  const [link = 0, dmg = 0, gen = 1] = d.n ?? [];
  let dealt = 0;
  for (const o of s.enemies) {
    if (o.hp <= 0 || o.linkId !== link || !((o.linkTicks ?? 0) > 0)) continue;
    o.uqGen = gen;
    dealt += damageEnemy(s, o, dmg, { pid: h.pid, type: 'shadow', noReact: true, linked: true });
    ev(s, { t: 'beam', x: d.x, y: d.y, tx: o.x, ty: o.y, kind: 'shareDeath' });
  }
  featUse(s, h, 'jailerSurcoat', dealt);
};

/** Cadenas que buscan: the chain of the one that died passes to the nearest unbound nightmare. */
const chainSeek = (s: BattleState, h: Hero, d: UqDue): void => {
  const [link = 0, ticks = 0, bp = 0] = d.n ?? [];
  const range = formFx('jailerSurcoat', 'final').range as number;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of hits(s, d.x, d.y, range)) {
    if (o.kind === 'boneWall' || ((o.linkTicks ?? 0) > 0 && o.linkId === link)) continue;
    const dd = dist2(o.x, o.y, d.x, d.y);
    if (dd < bd) {
      bd = dd;
      best = o;
    }
  }
  if (!best) return;
  best.linkId = link;
  best.linkTicks = ticks;
  best.linkBp = bp;
  best.jailBy = h.id;
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: best.x, ty: best.y, kind: 'shadowChain' });
};

/* ---------- Capa del mensajero (Parpadeo) ---------- */

/** Imagen: where the mage stood, an image of it draws the nightmares near a moment. */
export const leftImage = (s: BattleState, h: Hero): void => {
  const f = formFx('messengerCape', 'final');
  const hp = imax(1, idiv(h.stats.maxHp, 2));
  const b: Bot = { id: s.nextId++, owner: h.id, pid: h.pid, kind: 'decoy', x: h.x, y: h.y, hp, maxHp: hp, damage: 0, radius: 0, every: 0, cd: 0, expires: s.tick + (f.ticks as number), taunt: f.taunt as number, aura: 0, auraBp: 0, facing: h.facing, anim: 'idle', animT: 0, uq: 'messengerCape' };
  (s.bots ??= []).push(b);
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 600, kind: 'mirrorImage' });
};

/** Llamarada doble: another flare where the mage lands (`first`: what the first one reached). */
export const arrivalFlare = (s: BattleState, h: Hero, dmg: number, radius: number, type: DamageType, slow: { bp: number; ticks: number } | null, first: number): void => {
  if (!hasUq(h, 'messengerCape')) return;
  const blow = mulBp(dmg, fx('messengerCape', h).damageBp as number);
  let n = 0;
  for (const e of hits(s, h.x, h.y, radius)) {
    damageEnemy(s, e, blow, type === 'physical' ? { pid: h.pid } : { pid: h.pid, type });
    if (slow) {
      if (e.slowBp < slow.bp) e.slowBp = slow.bp;
      if (e.slowTicks < slow.ticks) e.slowTicks = slow.ticks;
    }
    n++;
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r: radius, kind: type === 'frost' ? 'blinkFrost2' : 'blink2' });
  if (n >= FEAT.need.flare) featUse(s, h, 'messengerCape', 1);
  if (n >= FEAT.need.flares && first >= FEAT.need.flares) featPieceTest(s, h, 'messengerCape');
};

/* ---------- Lágrima de hielo (Ventisca) ---------- */

/** Granizo: every so often a hailstone strikes a chilled one inside (lightning: it shatters it; never the dragon). */
const hailTick = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('iceTear', h);
  (z.uqn ??= [z.ticks + 1, 0, 0]);
  if (z.ticks <= 0 || z.ticks % (f.every as number) !== 0) return;
  const e = enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && (o.chill ?? 0) > s.tick && !(s.dragon && o.id === s.dragon.id))[0];
  if (!e) return;
  damageEnemy(s, e, f.damage as number, { pid: z.pid, type: 'lightning' });
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'hail' });
  featUse(s, h, 'iceTear', 1);
  z.kills = (z.kills ?? 0) + 1;
  if (z.kills === FEAT.need.hail) featPieceTest(s, h, 'iceTear');
};

/* ---------- Piedra del sueño hondo (Grieta del sueño) ---------- */

/** The rift burst: it stays open (a rift that dragged enough in is the test). */
const riftStays = (s: BattleState, z: Zone, h: Hero): void => {
  if (z.hop) return;
  if (hits(s, z.x, z.y, z.radius).length >= FEAT.need.rift) featPieceTest(s, h, 'dreamStone');
  z.hop = true;
  z.ticks = fx('dreamStone', h).ticks as number;
  z.hit = [];
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'riftOpen' });
};

/** Grieta abierta: what comes in is stunned a moment (a piece's stun), once each; with its final form, marked for the blow. */
export const riftOpen = (s: BattleState, z: Zone): void => {
  if (s.tick % 3 !== 0) return;
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  const f = fx('dreamStone', h);
  const fin = formFinal(h, 'dreamStone');
  const seen = (z.hit ??= []);
  let n = 0;
  for (const e of hits(s, z.x, z.y, z.radius)) {
    if (seen.includes(e.id)) continue;
    seen.push(e.id);
    const st = pieceStun(e, f.stun as number);
    if (st <= 0) continue;
    if (st > e.stun) e.stun = st;
    n++;
    if (fin) e.dreamBy = h.id;
  }
  featUse(s, h, 'dreamStone', n);
};

/** Golpe en el sueño: the first blow on one the open rift stunned hits harder. */
export const dreamBlow = (e: Enemy, amount: number): number => {
  e.dreamBy = undefined;
  return mulBp(amount, formFx('dreamStone', 'final').bp as number);
};

/* ---------- Brasa del cometa (Lluvia de fuego) ---------- */

/** A meteor of the ember: on the biggest nightmare inside not struck yet this rain (never the dragon: fire), else at random. */
export const cometMeteor = (s: BattleState, z: Zone): void => {
  const h = heroOfPid(s, z.pid);
  const seen = (z.hit ??= []);
  let best: Enemy | null = null;
  for (const pass of [0, 1]) {
    for (const e of hits(s, z.x, z.y, z.radius)) {
      if ((s.dragon && e.id === s.dragon.id) || (pass === 0 && seen.includes(e.id))) continue;
      if (!best || e.maxHp > best.maxHp) best = e;
    }
    if (best || !seen.length) break;
    seen.length = 0;
  }
  let x: number;
  let y: number;
  if (best) {
    seen.push(best.id);
    x = best.x;
    y = best.y;
  } else {
    const a = rngInt(s.rng, 1024);
    const d = rngInt(s.rng, z.radius + 1);
    x = z.x + idiv(cosA(a) * d, 10000);
    y = z.y + idiv(sinA(a) * d, 10000);
  }
  const blast = z.blast ?? 900;
  const dmg = h ? mulBp(z.damage, fx('cometEmber', h).damageBp as number) : z.damage;
  const type = z.dt ?? 'fire';
  const fin = h && z.ticks < z.every && formFinal(h, 'cometEmber') ? formFx('cometEmber', 'final') : null;
  if (fin && h) {
    // Cola del cometa: the last one is huge, striking again and again over a wide ring.
    for (let i = 1; i <= (fin.hits as number); i++) due(s, { at: s.tick + i * (fin.every as number), k: 'cometTail', hid: h.id, x, y, n: [dmg, mulBp(blast, fin.blastBp as number), type === 'fire' ? 0 : 1] });
    ev(s, { t: 'boom', x, y, r: mulBp(blast, fin.blastBp as number), kind: 'cometWarn' });
    return;
  }
  let dealt = 0;
  let felled = 0;
  for (const e of hits(s, x, y, blast)) {
    dealt += damageEnemy(s, e, dmg, type === 'physical' ? { pid: z.pid } : { pid: z.pid, type });
    if (e.hp <= 0) felled++;
  }
  ev(s, { t: 'boom', x, y, r: blast, kind: 'meteor' });
  if (!h) return;
  rainCount(s, z, h, dealt, felled);
};

const rainCount = (s: BattleState, z: Zone, h: Hero, dealt: number, felled: number): void => {
  featUse(s, h, 'cometEmber', dealt);
  const t = (z.uqn ??= [0, 0]);
  t[0] = (t[0] ?? 0) + felled;
  if (!t[1] && (t[0] ?? 0) >= FEAT.need.rainKills) {
    t[1] = 1;
    featPieceTest(s, h, 'cometEmber');
  }
};

const cometTail = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, r = 0, phys = 0] = d.n ?? [];
  let dealt = 0;
  for (const e of hits(s, d.x, d.y, r)) dealt += damageEnemy(s, e, dmg, phys ? { pid: h.pid } : { pid: h.pid, type: 'fire' });
  ev(s, { t: 'boom', x: d.x, y: d.y, r, kind: 'cometTail' });
  featUse(s, h, 'cometEmber', dealt);
};

/* ---------- each zone, each death, the queue ---------- */

/** Every tick, for a zone of a mage's piece (after its own ground did its part): what the piece adds, and its end. */
export const mageZone = (s: BattleState, z: Zone): void => {
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  if (z.uq === 'distillerStaff') dropTick(s, z, h);
  else if (z.uq === 'pantryStaff') trailTick(s, z, h);
  else if (z.uq === 'whiteWallRobe') {
    if (z.ticks <= 0) wallBursts(s, z, h);
  } else if (z.uq === 'iceTear') hailTick(s, z, h);
  else if (z.uq === 'dreamStone') {
    if (z.ticks <= 0) riftStays(s, z, h);
  }
};

/** A nightmare died: the distiller's dart flies on, the jailer's binding shares its death, the tear's storm feeds. */
export const mageDied = (s: BattleState, e: Enemy): void => {
  const gen = e.uqGen ?? 0;
  if (e.dripBy !== undefined && (e.acidTicks ?? 0) > 0 && gen < GENS) {
    const h = heroById(s, e.dripBy);
    if (h && formFinal(h, 'distillerStaff')) due(s, { at: s.tick + 1, k: 'dartAgain', hid: h.id, x: e.x, y: e.y, n: [e.dripDmg ?? 0, gen + 1] });
  }
  if (e.jailBy !== undefined && (e.linkTicks ?? 0) > 0 && e.linkId !== undefined) {
    const h = heroById(s, e.jailBy);
    if (h) {
      if (gen < GENS) due(s, { at: s.tick + 1, k: 'shareDeath', hid: h.id, x: e.x, y: e.y, n: [e.linkId, mulBp(e.maxHp, fx('jailerSurcoat', h).shareBp as number), gen + 1] });
      if (formFinal(h, 'jailerSurcoat')) due(s, { at: s.tick + 1, k: 'chainSeek', hid: h.id, x: e.x, y: e.y, n: [e.linkId, e.linkTicks ?? 0, e.linkBp ?? 0] });
    }
  }
  for (const z of s.zones) {
    if (z.kind !== 'blizzard' || z.uq !== 'iceTear' || z.ticks <= 0 || !withinRange(z.x, z.y, e.x, e.y, z.radius)) continue;
    const h = heroOfPid(s, z.pid);
    if (!h || !formFinal(h, 'iceTear')) continue;
    // Ventisca que se alimenta: longer with each death inside, up to its share of what it first lasted.
    const f = formFx('iceTear', 'final');
    const t = (z.uqn ??= [z.ticks, 0, 0]);
    const room = mulBp(t[0] ?? 0, f.maxBp as number) - (t[0] ?? 0) - (t[1] ?? 0);
    const add = imin(f.ticks as number, room);
    if (add > 0) {
      z.ticks += add;
      t[1] = (t[1] ?? 0) + add;
    }
  }
};

/** What the mage's pieces left for this tick. False: not one of theirs. */
export const mageDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'boltBack') boltBackGo(s, h, d);
  else if (d.k === 'boltSplit') boltSplit(s, h, d);
  else if (d.k === 'drip') dripFalls(s, h, d);
  else if (d.k === 'dartAgain') dartAgain(s, h, d);
  else if (d.k === 'shareDeath') shareDeath(s, h, d);
  else if (d.k === 'chainSeek') chainSeek(s, h, d);
  else if (d.k === 'cometTail') cometTail(s, h, d);
  else return false;
  return true;
};
